import { useEffect } from 'react';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const privacySchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Privacy Policy — TWINODE',
  url: `${SITE_URL}/privacy`,
  description:
    'TWINODE Privacy Policy. Zero-Trust architecture, no data storage after delivery, and no third-party sharing. Your Executive DNA parameters are encrypted and purged after audit delivery.',
  isPartOf: {
    '@type': 'WebSite',
    name: 'TWINODE',
    url: SITE_URL,
  },
  inLanguage: 'en-US',
};

export default function Privacy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.querySelectorAll('script[data-schema="privacy"]').forEach((s) => s.remove());
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'privacy');
    script.textContent = JSON.stringify(privacySchema);
    document.head.appendChild(script);
    return () => {
      document.querySelectorAll('script[data-schema="privacy"]').forEach((s) => s.remove());
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <a 
            href="/"
            className="flex items-center space-x-2 hover:opacity-70 transition-opacity cursor-pointer w-fit"
          >
            <img 
              src="https://static.readdy.ai/image/378dd0f6400ec53d43ae7d1d7dd92295/8acfdc25fd3b0de57798749a193ed73d.png" 
              alt="TwiNode Logo" 
              className="h-8 w-8 object-contain"
            />
            <span className="text-lg font-bold">TwiNode</span>
          </a>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-sm text-gray-500">Last updated: January 2025</p>
        </div>

        <div className="prose prose-lg max-w-none">
          {/* Introduction */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4">Our Commitment to Your Privacy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              At TwiNode, we understand that your personal and commercial data is sensitive. Our system is built on a <strong>Zero-Trust architecture</strong> designed to protect your information at every stage of the computational pipeline.
            </p>
            <p className="text-gray-700 leading-relaxed">
              This Privacy Policy outlines how we collect, process, and protect your data when you use our strategic audit services.
            </p>
          </section>

          {/* Data Collection */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4">1. What Data We Collect</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To generate your personalized 10-page strategic audit, we collect the following information:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Email Address:</strong> Used for whitelist verification and encrypted delivery of your audit report.</li>
              <li><strong>Birth Coordinates:</strong> Precise date, time, and location data required for spatiotemporal calibration and Bazi matrix computation.</li>
              <li><strong>Commercial Variables:</strong> Optional business context you provide to enhance the strategic simulation accuracy.</li>
            </ul>
          </section>

          {/* How We Use Data */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4">2. How We Use Your Data</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Your data is used exclusively for the following purposes:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Computational Processing:</strong> Your Executive DNA parameters are encrypted during transit and processed through our 10-node decoupled pipeline to generate your strategic audit.</li>
              <li><strong>Whitelist Verification:</strong> Your email is cross-referenced against our secure database (Database A) to authorize access to expensive computational resources.</li>
              <li><strong>Encrypted Delivery:</strong> Your completed PDF audit is delivered to your authorized email address via automated, encrypted channels.</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              <strong>We do NOT:</strong>
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Sell, rent, or share your personal data with third parties for marketing purposes.</li>
              <li>Store your birth coordinates or commercial variables after your audit is delivered.</li>
              <li>Use your data for any purpose beyond generating your personalized strategic audit.</li>
            </ul>
          </section>

          {/* Zero-Trust Architecture */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4">3. Zero-Trust Architecture</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our system operates on a <strong>Zero-Trust security model</strong>:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Encrypted Transit:</strong> All data transmitted between your browser and our servers is encrypted using industry-standard TLS/SSL protocols.</li>
              <li><strong>Whitelist-Only Access:</strong> Only authorized emails (verified via payment) can trigger the computational pipeline. Unauthorized requests are terminated immediately.</li>
              <li><strong>State Lock Mechanism:</strong> After your audit is delivered, your authorization token is locked to prevent duplicate submissions and unauthorized access.</li>
              <li><strong>No Persistent Storage:</strong> Your birth coordinates and commercial variables are purged from our systems immediately after your PDF is generated and delivered.</li>
            </ul>
          </section>

          {/* Data Retention */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4">4. Data Retention</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We retain only the minimum data necessary to provide our services:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Email Address:</strong> Stored in our whitelist database (Database A) to verify authorization and prevent abuse. This data is retained for the duration of your service access.</li>
              <li><strong>Birth Coordinates & Commercial Variables:</strong> Processed in real-time and <strong>deleted immediately</strong> after your audit is delivered. We do not retain this data.</li>
              <li><strong>Payment Information:</strong> Processed securely via Lemon Squeezy. We do not store your credit card or payment details on our servers.</li>
            </ul>
          </section>

          {/* Third-Party Services */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4">5. Third-Party Services</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use the following trusted third-party services to deliver our platform:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Lemon Squeezy:</strong> Payment processing. Your payment data is handled securely by Lemon Squeezy and is not stored on our servers.</li>
              <li><strong>Tally:</strong> Secure form intake for your birth coordinates and commercial variables. Data is transmitted directly to our backend via encrypted webhooks.</li>
              <li><strong>OpenAI (GPT-4o):</strong> AI-powered strategic translation. Your raw Bazi matrix is processed by OpenAI's API to generate behavioral economics insights. OpenAI does not retain your data beyond the API call.</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              All third-party services are bound by their respective privacy policies and security standards.
            </p>
          </section>

          {/* Your Rights */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4">6. Your Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You have the following rights regarding your personal data:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Access:</strong> You may request a copy of the data we hold about you (limited to your email address in our whitelist database).</li>
              <li><strong>Deletion:</strong> You may request deletion of your email from our whitelist database at any time. Note that this will revoke your access to future audits.</li>
              <li><strong>Correction:</strong> If your email address is incorrect, please contact us to update it.</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              To exercise any of these rights, please contact us at: <a href="mailto:privacy@twinode.com" className="text-black font-semibold hover:underline cursor-pointer">privacy@twinode.com</a>
            </p>
          </section>

          {/* Security */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4">7. Security Measures</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We implement industry-standard security measures to protect your data:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>End-to-end encryption for all data in transit</li>
              <li>Secure, isolated database architecture with whitelist-only access</li>
              <li>Regular security audits and penetration testing</li>
              <li>Automated state lock mechanisms to prevent replay attacks</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              While we take every precaution to protect your data, no system is 100% secure. We encourage you to use strong passwords and keep your email account secure.
            </p>
          </section>

          {/* Changes to Policy */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4">8. Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. Any updates will be posted on this page with a revised "Last updated" date. We encourage you to review this policy periodically.
            </p>
          </section>

          {/* Contact */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4">9. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions or concerns about this Privacy Policy or our data practices, please contact us:
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Email:</strong> <a href="mailto:privacy@twinode.com" className="text-black font-semibold hover:underline cursor-pointer">privacy@twinode.com</a>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-20">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <img 
                src="https://static.readdy.ai/image/378dd0f6400ec53d43ae7d1d7dd92295/8acfdc25fd3b0de57798749a193ed73d.png" 
                alt="TwiNode Brand Logo" 
                className="h-6 w-6 object-contain"
              />
              <span className="font-bold text-black">TwiNode</span>
            </div>
            <div>
              © 2025 TwiNode. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}